import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, useSpring } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import SEOHead, { breadcrumbSchema } from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Word-by-word headline reveal ─── */
const AnimatedHeadline = ({ text }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-10%' });
  const words = text.split(' ');

  return (
    <h2 ref={ref} className="font-presti text-2xl md:text-4xl lg:text-5xl text-black/90 leading-tight mb-8">
      {words.map((word, i) => (
        <span key={i} className="inline-block overflow-hidden mr-[0.3em]">
          <motion.span
            className="inline-block"
            initial={{ y: '110%', rotate: 3 }}
            animate={isInView ? { y: 0, rotate: 0 } : {}}
            transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
          >
            {word}
          </motion.span>
        </span>
      ))}
    </h2>
  );
};

/* ─── Slide reveal ─── */
const SlideReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-10%' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, x: -40, filter: 'blur(8px)' }} animate={isInView ? { opacity: 1, x: 0, filter: 'blur(0px)' } : {}} transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
};

/* ─── Fade up ─── */
const FadeUp = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-15%' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 50 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
};

/* ─── Counter Animation ─── */
const AnimatedCounter = ({ value, suffix = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-10%' });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const end = parseInt(value);
    if (isNaN(end)) { setCount(value); return; }
    const duration = 1500;
    const step = (end / duration) * 16;
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, value]);
  return <span ref={ref}>{count}{suffix}</span>;
};

/* ─── Parallax Image ─── */
const ParallaxImage = ({ src, alt = '', focalDesktop, focalMobile, isMobile }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.15, 1, 1.05]);
  const smoothY = useSpring(y, { stiffness: 100, damping: 30 });
  const imgRef = useRef(null);
  const isInView = useInView(imgRef, { once: true, margin: '-5%' });

  const fp = isMobile ? (focalMobile || { x: 50, y: 50 }) : (focalDesktop || { x: 50, y: 50 });

  return (
    <div ref={ref} className="relative w-full overflow-hidden rounded-sm" style={{ aspectRatio: '4/3' }}>
      <motion.div ref={imgRef} className="absolute inset-0" style={{ y: smoothY, scale }}>
        <motion.img
          src={src} alt={alt}
          className="w-full h-full object-cover"
          style={{ objectPosition: `${fp.x}% ${fp.y}%` }}
          initial={{ opacity: 0, scale: 1.2 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        />
      </motion.div>
    </div>
  );
};

/* ─── Accent line ─── */
const AccentLine = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-10%' });
  return (
    <motion.div ref={ref} className="h-[1px] bg-gradient-to-r from-black/20 to-transparent" initial={{ width: 0 }} animate={isInView ? { width: '4rem' } : {}} transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }} />
  );
};

/* ─── Section ─── */
const DifferenceSection = ({ section, index, isMobile }) => {
  const isEven = index % 2 === 0;
  return (
    <section className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid={`cd-section-${section.number}`}>
      <div className="max-w-6xl mx-auto">
        <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 lg:gap-20 items-center`}>
          <div className="w-full lg:w-5/12">
            <FadeUp delay={0.1}>
              {section.image_url ? (
                <ParallaxImage src={section.image_url} alt={section.headline} focalDesktop={section.image_focal_desktop} focalMobile={section.image_focal_mobile} isMobile={isMobile} />
              ) : (
                <div className="w-full bg-black/5 rounded-sm flex items-center justify-center" style={{ aspectRatio: '4/3' }}>
                  <span className="text-black/20 text-sm">No image</span>
                </div>
              )}
            </FadeUp>
          </div>
          <div className="w-full lg:w-7/12">
            <FadeUp>
              <span className="text-black/10 text-6xl md:text-8xl font-thin leading-none select-none block mb-6 font-presti">{section.number}</span>
            </FadeUp>
            <AnimatedHeadline text={section.headline} />
            <SlideReveal delay={0.2}>
              <p className="text-black/50 text-base md:text-lg leading-relaxed max-w-2xl mb-4">{section.body}</p>
            </SlideReveal>
            {section.sub_body && (
              <SlideReveal delay={0.35}>
                <p className="text-black/35 text-sm md:text-base leading-relaxed max-w-2xl">{section.sub_body}</p>
              </SlideReveal>
            )}
            <div className="mt-10"><AccentLine /></div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/3 h-[1px] bg-gradient-to-r from-transparent via-black/5 to-transparent" />
    </section>
  );
};

/* ─── Main Page ─── */
const CrystalDifferencePage = () => {
  const [content, setContent] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const progressWidth = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cdRes, settingsRes] = await Promise.all([
          fetch(`${API_URL}/api/content/crystal-difference`),
          fetch(`${API_URL}/api/content/settings`).catch(() => ({ json: () => ({}) })),
        ]);
        const cdData = await cdRes.json();
        const settingsData = await settingsRes.json();
        setContent(cdData);
        setLogoUrl(settingsData.photo_logo_url || settingsData.logo_url || '');
      } catch (e) {
        console.error('Failed to fetch Crystal Difference data:', e);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, []);

  if (!content) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ repeat: Infinity, duration: 2 }}>
          <span className="font-presti text-black/40 text-lg tracking-[0.3em]">LOADING</span>
        </motion.div>
      </div>
    );
  }

  const sections = (content.sections || []).sort((a, b) => a.order - b.order);
  const deliverables = content.deliverables || [];
  const stats = (content.stats && content.stats.length > 0) ? content.stats : [
    { value: '14', suffix: '+', label: 'Years' },
    { value: '25', suffix: '+', label: 'Specialists' },
    { value: '100', suffix: '%', label: 'Completion' },
    { value: '100', suffix: '%', label: 'Satisfaction' },
  ];
  const heroBg = content.hero_bg_image_url || '';
  const heroFp = isMobile ? (content.hero_bg_focal_mobile || { x: 50, y: 50 }) : (content.hero_bg_focal_desktop || { x: 50, y: 50 });

  return (
    <div ref={containerRef} className="bg-white min-h-screen" data-testid="crystal-difference-page">
      <SEOHead
        title="Why Choose Crystal Media — The Crystal Difference"
        description="Discover why 1000+ couples trust Crystal Media for their wedding photography and cinematography. 14+ years of experience capturing Pakistani, Indian, Punjabi, Sikh, Muslim, and multicultural weddings with cinematic precision across Toronto, Vancouver, Montreal, and North America."
        url="/crystal-difference"
        keywords={['Why Choose Crystal Media', 'Best Wedding Photographer Toronto', 'Top Wedding Videographer Canada']}
        jsonLd={breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Why Us', path: '/crystal-difference' }])}
      />
      <motion.div className="fixed top-0 left-0 right-0 h-[2px] bg-black z-[60] origin-left" style={{ width: progressWidth }} />
      <Header logoUrl={logoUrl} variant="light" />

      {/* ─── HERO ─── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-6 overflow-hidden" data-testid="cd-hero">
        {/* Background image */}
        {heroBg && (
          <>
            <img
              src={heroBg} alt="" className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: `${heroFp.x}% ${heroFp.y}%` }}
            />
            <div className="absolute inset-0" style={{ backgroundColor: content.hero_overlay_color || '#000000', opacity: content.hero_overlay_opacity || 0.3 }} />
          </>
        )}
        {!heroBg && <div className="absolute inset-0 bg-gradient-to-b from-[#f8f8f8] to-white" />}

        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} className="text-center relative z-10">
          <motion.p initial={{ opacity: 0, letterSpacing: '0.2em' }} animate={{ opacity: 1, letterSpacing: '0.4em' }} transition={{ duration: 1.5, delay: 0.3 }} className={`text-[10px] md:text-xs uppercase mb-6 tracking-[0.4em] ${heroBg ? 'text-white/50' : 'text-black/30'}`}>
            {content.hero_subtitle}
          </motion.p>
          <h1 className={`font-presti text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-[0.02em] leading-[0.95] mb-8 ${heroBg ? 'text-white' : 'text-black/90'}`}>
            {content.hero_title.split(/\s+/).reduce((acc, word, i, arr) => {
              if (i === Math.ceil(arr.length / 2)) return [...acc, <br key="br" />, word + ' '];
              return [...acc, word + ' '];
            }, [])}
          </h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 1 }} className={`text-sm md:text-base max-w-md mx-auto leading-relaxed ${heroBg ? 'text-white/60' : 'text-black/40'}`}>
            {content.hero_description}
          </motion.p>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }} className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
          <span className={`text-[9px] tracking-[0.3em] uppercase ${heroBg ? 'text-white/30' : 'text-black/20'}`}>Scroll</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }} className={`w-[1px] h-8 bg-gradient-to-b ${heroBg ? 'from-white/40' : 'from-black/30'} to-transparent`} />
        </motion.div>
      </section>

      {/* ─── STATS (no boxes) ─── */}
      {stats.length > 0 && (
        <FadeUp className="py-16 px-6 border-y border-black/5">
          <div className={`max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-${Math.min(stats.length, 4)} gap-8 md:gap-12`} data-testid="cd-stats">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="font-presti text-4xl md:text-5xl text-black/80 mb-2">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </p>
                <p className="text-[10px] tracking-[0.25em] uppercase text-black/30">{stat.label}</p>
              </div>
            ))}
          </div>
        </FadeUp>
      )}

      {/* ─── CONTENT SECTIONS ─── */}
      {sections.map((section, index) => (
        <DifferenceSection key={section.id || index} section={section} index={index} isMobile={isMobile} />
      ))}

      {/* ─── WHAT YOU GET ─── */}
      <section className="py-32 px-6 md:px-12 lg:px-24 relative bg-gradient-to-b from-white to-[#f8f8f8]" data-testid="cd-deliverables">
        <div className="max-w-3xl mx-auto">
          <FadeUp>
            <p className="text-[10px] tracking-[0.4em] uppercase text-black/30 mb-4">The Promise</p>
            <h2 className="font-presti text-4xl sm:text-5xl md:text-6xl text-black/90 leading-[1.05] mb-16">
              What you get<br />when you hire us
            </h2>
          </FadeUp>
          <div className="space-y-0">
            {deliverables.map((item, i) => (
              <FadeUp key={i} delay={i * 0.06}>
                <div className="flex items-start gap-5 py-6 border-b border-black/5 group" data-testid={`cd-deliverable-${i}`}>
                  <motion.span className="text-black/15 font-presti text-sm mt-1 flex-shrink-0 w-8" whileHover={{ color: 'rgba(0,0,0,0.7)' }}>0{i + 1}</motion.span>
                  <p className="text-black/50 text-base md:text-lg group-hover:text-black/80 transition-colors duration-300">{item}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA (Inquire Now only) ─── */}
      <section className="py-32 flex items-center justify-center px-6 relative overflow-hidden" data-testid="cd-cta">
        <FadeUp className="text-center relative z-10">
          <h2 className="font-presti text-3xl sm:text-4xl md:text-5xl text-black/90 mb-6">{content.cta_heading}</h2>
          <p className="text-black/40 text-sm md:text-base max-w-md mx-auto mb-10 leading-relaxed">{content.cta_description}</p>
          <Link to="/inquire" className="group inline-flex items-center gap-3 px-10 py-4 bg-black text-white text-xs tracking-[0.2em] uppercase font-medium hover:bg-black/80 transition-all" data-testid="cd-cta-inquire">
            Inquire Now
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </FadeUp>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="py-12 px-6 border-t border-black/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-black/30">{new Date().getFullYear()} Crystal Media. Toronto, Canada.</p>
          <div className="flex items-center gap-6">
            <Link to="/photo" className="text-[10px] tracking-[0.2em] uppercase text-black/30 hover:text-black transition-colors">Photo</Link>
            <Link to="/film" className="text-[10px] tracking-[0.2em] uppercase text-black/30 hover:text-black transition-colors">Film</Link>
            <Link to="/about" className="text-[10px] tracking-[0.2em] uppercase text-black/30 hover:text-black transition-colors">About</Link>
            <Link to="/inquire" className="text-[10px] tracking-[0.2em] uppercase text-black/30 hover:text-black transition-colors">Inquire</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CrystalDifferencePage;
